import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Plus, X } from 'lucide-react';

export interface ProjectMapping {
  repoName: string;
  projectKey: string;
}

interface ProjectMappingsEditorProps {
  mappings: ProjectMapping[];
  onChange: (mappings: ProjectMapping[]) => void;
  label: string;
  projectKeyLabel: string;
  gitRepoNames?: string[];
}

export default function ProjectMappingsEditor({ mappings, onChange, label, projectKeyLabel, gitRepoNames = [] }: ProjectMappingsEditorProps) {
  const handleAdd = () => {
    const mappedRepos = mappings.map(m => m.repoName);
    const unmappedRepos = gitRepoNames.filter(name => !mappedRepos.includes(name));

    if (unmappedRepos.length > 0) {
      const newMappings = unmappedRepos.map(repoName => ({ repoName, projectKey: '' }));
      onChange([...mappings, ...newMappings]);
    } else {
      onChange([...mappings, { repoName: '', projectKey: '' }]);
    }
  };

  const handleRemove = (index: number) => {
    onChange(mappings.filter((_, i) => i !== index));
  };

  const handleChange = (index: number, field: 'repoName' | 'projectKey', value: string) => {
    const updated = [...mappings];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  return (
    <Box sx={{ mt: 1.5, p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {label}
      </Typography>
      {mappings.map((mapping, index) => (
        <Stack key={index} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <TextField
            size="small"
            placeholder="Git repo name"
            value={mapping.repoName}
            onChange={(e) => handleChange(index, 'repoName', e.target.value)}
            sx={{ flex: 1 }}
            InputProps={{ sx: { fontSize: 12 } }}
          />
          <Typography variant="body2" color="text.secondary">→</Typography>
          <TextField
            size="small"
            placeholder={projectKeyLabel}
            value={mapping.projectKey}
            onChange={(e) => handleChange(index, 'projectKey', e.target.value)}
            sx={{ flex: 1 }}
            InputProps={{ sx: { fontSize: 12 } }}
          />
          <IconButton size="small" onClick={() => handleRemove(index)} color="error">
            <X size={14} />
          </IconButton>
        </Stack>
      ))}
      <Button size="small" startIcon={<Plus size={14} />} onClick={handleAdd} sx={{ mt: 0.5 }}>
        Add Mapping
      </Button>
    </Box>
  );
}
